import { normalizeFinishes } from "../domain/finishes/types";
/**
 * Bridge between `BuildInput` and the pure interior object model.
 *
 * The domain layer knows nothing about wall build-ups, roof pitches or
 * two-room partitions — it takes a room rectangle in millimetres. This module
 * derives that rectangle from a `BuildInput`.
 *
 * The heater is NOT re-derived here. `deriveHeaterClearanceLayout` is already
 * the single owner of "where does the engine put the aggregat", including the
 * awkward cases (panorama moves it to the opposite long wall; a two-room wood
 * heater with a relax room hangs off the divider, not at the placement domain's
 * Z; a one-room long entry nudges it clear of the door). Re-implementing that
 * decision tree here is exactly the mirrored-constant trap, so the heater box
 * is read back from it and converted into layout coordinates.
 *
 * Barrel saunas return `null`: the engine is deprecated for new designs
 * (issue 071) and keeps its generated interior.
 */

import { resolveWallHeightM } from "../domain/geometry/buildingDimensions";
import {
	type BenchAnchor,
	type BenchMaterialsResult,
	type BenchRun,
	createInteriorLayout,
	DEFAULT_BENCH_ANCHOR,
	deriveBenchMaterials,
	type HeaterPlacement,
	type HeaterSizeMm,
	heaterFootprint,
	type InteriorDoor,
	type InteriorLayout,
	type InteriorLayoutVariantId,
	type InteriorRoom,
	type InteriorWall,
	reconcileInteriorLayout,
	resolveEffectiveLayout,
	wallAxisLengthMm,
	wallAxisOriginMm,
	wallRunsAlongX,
} from "../domain/interior";
import { resolveDoorSwing } from "../domain/openings/openingPresets";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import type { SaunaType } from "../types/design";
import {
	type AuthoredBenchObstacle,
	type AuthoredInteriorGeometry,
	deriveHeaterClearanceLayout,
	type HeaterClearanceLayout,
	isTwoRoomInput,
} from "./heaterClearanceLayout";
import {
	createInteriorRenderPlan,
	type InteriorRenderPlan,
} from "./interiorRenderPlan";
import { createSquareBaseBuildContext } from "./squareBaseBuilder";
import {
	resolveBenchSupportExcludedWalls,
	resolveGlazedInteriorWalls,
	resolveOpeningAdjustmentOpenings,
} from "./twoRoomOpeningConfig";
import {
	createTwoRoomRoomLayout,
	getHotRoomBenchPlacement,
	getRelaxBenchPlacement,
	resolvePartitionSplit,
	resolveTwoRoomConfig,
} from "./twoRoomSauna";
import type { BuildInput } from "./types";

export interface InteriorLayoutContext {
	/** The hot room — the one that has lavar and an aggregat. */
	room: InteriorRoom;
	heaterSize: HeaterSizeMm;
	/** Where the engines stand the heater, in layout coordinates. */
	heater: HeaterPlacement;
	/** Which wall the engines hang the hot room's benches on. */
	benchAnchor: BenchAnchor;
	/** The generated layout's variant — an L for the panorama view layout. */
	benchVariant: InteriorLayoutVariantId;
	/**
	 * The relax room, when a divider creates one. Authored runs tagged
	 * `room: "relax"` are measured, billed and validated against THIS rectangle;
	 * without it they were filtered out of every derivation and silently cost
	 * nothing.
	 */
	relaxRoom: InteriorRoom | null;
	/**
	 * The relax bench the engine draws, expressed as a run. The generated default
	 * includes it, because it is rendered on every relax design and was billed by
	 * nothing — before or after the `lmLavar` heuristic.
	 */
	relaxBench: BenchRun | null;
	/**
	 * The whole finished interior, undivided. Needed to tell an EXTERIOR short
	 * wall from the divider: both are a room's front/back, and only the envelope
	 * says which is which.
	 */
	envelope: InteriorRoom;
}

/** Every room a layout may place runs in, hot room first. */
export function interiorRooms(context: InteriorLayoutContext): InteriorRoom[] {
	return context.relaxRoom ? [context.room, context.relaxRoom] : [context.room];
}

/**
 * Round a derived millimetre value to 0.1 mm.
 *
 * The engine works in metres, so `innerW / 2 * 1000` lands on values like
 * 449.99999999999994. Those float tails would be persisted verbatim in the
 * `interior_layout` jsonb, make saved designs compare unequal to freshly
 * generated ones, and churn every snapshot — for a precision no sawmill has.
 */
function roundMm(value: number): number {
	return Math.round(value * 10) / 10;
}

/**
 * Narrow the room to the hot side of a two-room divider. Returns null when the
 * design has no relax room, in which case the whole building IS the hot room.
 */
function twoRoomHotRoom(
	input: BuildInput,
	roofConfig: NonNullable<BuildInput["roofConfig"]>,
): {
	zRange: { minZ: number; maxZ: number } | null;
	relaxZRange: { minZ: number; maxZ: number } | null;
	benchAnchor: BenchAnchor;
	benchVariant: InteriorLayoutVariantId;
	/** The heater stands on the front end wall (the panorama view layout). */
	heaterOnFrontWall: boolean;
	relaxBench: BenchRun | null;
} {
	const config = resolveTwoRoomConfig(input);
	const L = input.dimensions.length;
	const ctx = createSquareBaseBuildContext(input.dimensions.width, L, {
		roofConfig,
		wallHeightM: input.wallHeightM,
	});
	const roomLayout = createTwoRoomRoomLayout(
		ctx,
		resolvePartitionSplit(input.partitionSplit, L, config.hasRelaxRoom),
		config,
	);
	const bench = getHotRoomBenchPlacement(roomLayout, {
		openings: resolveEngineOpenings(input),
		lengthM: L,
	});
	const relaxBench = getRelaxBenchPlacement(roomLayout, config);
	return {
		relaxBench: relaxBench
			? {
					id: "bench-relax",
					room: "relax",
					wall: relaxBench.wall,
					// Not a lav — an ordinary seat, so it stands on its own legs
					// rather than either sauna tier's default.
					tier: "lower",
					startMm: relaxBench.startMm,
					lengthMm: relaxBench.lengthMm,
					depthMm: relaxBench.depthMm,
					offsetFromWallMm: relaxBench.offsetFromWallMm,
					legHeightMm: relaxBench.legHeightMm,
				}
			: null,
		// Lavar live in the hot room only; the relax side is a separate room with
		// its own (still engine-generated) furniture. Without a divider the whole
		// building IS the hot room, so the envelope's own bounds stand.
		zRange: roomLayout.hasRelaxRoom
			? { minZ: roomLayout.saunaMinZ, maxZ: roomLayout.saunaMaxZ }
			: null,
		relaxZRange: roomLayout.hasRelaxRoom
			? { minZ: roomLayout.relaxMinZ, maxZ: roomLayout.relaxMaxZ }
			: null,
		benchAnchor: {
			wall: bench.wall,
			offsetFromWallMm: bench.offsetFromWallMm,
			endMm: bench.endMm,
			wrapTiers: bench.wrapTiers,
		},
		benchVariant: bench.variant,
		heaterOnFrontWall: roomLayout.viewLayout,
	};
}

/**
 * Derive the hot room, and where the engines put the heater in it, from a
 * build input. Returns null for engines without a layout-driven interior.
 */
export function deriveInteriorLayoutContext(
	input: BuildInput,
): InteriorLayoutContext | null {
	if (input.roofConfig?.kind === "barrel") return null;

	const twoRoom = isTwoRoomInput(input);
	const saunaType: SaunaType = twoRoom ? "tvarum" : "fyrkantig";
	const roofConfig = input.roofConfig ?? DEFAULT_ROOF_CONFIG_BY_TYPE[saunaType];
	if (roofConfig.kind === "barrel") return null;

	const clearance = deriveHeaterClearanceLayout(input);
	if (!clearance) return null;

	// The clearance layout already measured the finished room off the build
	// context; re-deriving `innerW`/`innerL` here would put the wall stack's
	// thicknesses in two places, which is what the mirrored-constants note is
	// about. Only the two-room hot-room narrowing needs anything more.
	const inner = clearance.roomInnerBox;
	const hotRoom = twoRoom ? twoRoomHotRoom(input, roofConfig) : null;
	const saunaZ = hotRoom?.zRange ?? null;

	const room: InteriorRoom = {
		id: "sauna",
		minXMm: roundMm(inner.minX * 1000),
		maxXMm: roundMm(inner.maxX * 1000),
		minZMm: roundMm((saunaZ?.minZ ?? inner.minZ) * 1000),
		maxZMm: roundMm((saunaZ?.maxZ ?? inner.maxZ) * 1000),
		benchSupportExcludedWalls: resolveBenchSupportExcludedWalls(input),
		glazedWalls: resolveGlazedInteriorWalls(input),
		wallHeightMm: roundMm(resolveWallHeightM(input.wallHeightM) * 1000),
	};

	const box = clearance.heaterBox;
	const heaterCenterXMm = roundMm(((box.minX + box.maxX) / 2) * 1000);
	const heaterCenterZMm = roundMm(((box.minZ + box.maxZ) / 2) * 1000);
	// Both engines stand the heater against a long wall; which one depends on
	// panorama glazing, so read it off the box rather than re-deriving it. The
	// panorama view layout backs it onto the front end wall instead.
	const wall: InteriorWall = hotRoom?.heaterOnFrontWall
		? "front"
		: heaterCenterXMm >= 0
			? "right"
			: "left";

	return {
		room,
		// The unit's own footprint, NOT oriented against a wall: an authored layout
		// may stand it on a short wall, and `heaterFootprint` re-orients per
		// placement. Storing the oriented pair swapped width and depth there.
		heaterSize: {
			widthMm: roundMm((box.maxX - box.minX) * 1000),
			depthMm: roundMm((box.maxZ - box.minZ) * 1000),
		},
		// A sauna at the +Z end of a two-room hangs its benches on the FRONT wall;
		// assuming "back" put the generated default against the divider.
		benchAnchor: hotRoom?.benchAnchor ?? DEFAULT_BENCH_ANCHOR,
		benchVariant: hotRoom?.benchVariant ?? "two-tier",
		relaxRoom: hotRoom?.relaxZRange
			? {
					id: "relax",
					minXMm: room.minXMm,
					maxXMm: room.maxXMm,
					minZMm: roundMm(hotRoom.relaxZRange.minZ * 1000),
					maxZMm: roundMm(hotRoom.relaxZRange.maxZ * 1000),
				}
			: null,
		relaxBench: hotRoom?.relaxBench ?? null,
		envelope: {
			id: "sauna",
			minXMm: roundMm(inner.minX * 1000),
			maxXMm: roundMm(inner.maxX * 1000),
			minZMm: roundMm(inner.minZ * 1000),
			maxZMm: roundMm(inner.maxZ * 1000),
		},
		heater:
			wall === "front"
				? {
						wall,
						offsetMm: roundMm(heaterCenterXMm - room.minXMm),
						wallGapMm: Math.max(0, roundMm(room.maxZMm - box.maxZ * 1000)),
					}
				: {
						wall,
						offsetMm: roundMm(heaterCenterZMm - room.minZMm),
						wallGapMm: Math.max(
							0,
							roundMm(
								wall === "right"
									? room.maxXMm - box.maxX * 1000
									: box.minX * 1000 - room.minXMm,
							),
						),
					},
	};
}

/**
 * The layout a calculation should use: the user's if they authored one,
 * otherwise the generated default. Either way it is reconciled against the
 * room first, so a design saved at 2.5 × 2.0 and reopened at 2.0 × 2.0 has its
 * runs trimmed and its heater moved to the nearest position that fits — never
 * a corrupt intermediate.
 */
export function resolveInteriorLayout(
	input: BuildInput,
	context?: InteriorLayoutContext | null,
): InteriorLayout | null {
	const ctx = context ?? deriveInteriorLayoutContext(input);
	if (!ctx) return null;

	const layout =
		input.interiorLayout ??
		createInteriorLayout(ctx.room, {
			variant: ctx.benchVariant,
			heater: ctx.heater,
			benchAnchor: ctx.benchAnchor,
			// The engine draws this bench unconditionally; generating it here is
			// what puts it on the material list at all.
			extraRuns: ctx.relaxBench ? [ctx.relaxBench] : [],
		});

	return reconcileInteriorLayout(layout, interiorRooms(ctx), ctx.heaterSize);
}

/**
 * Doors that open into the hot room, in the layout's wall-relative
 * coordinates.
 *
 * Two corrections over reading `input.openings` directly:
 *
 * 1. **Resolved, not raw.** A two-room with `entryWall: "long"` has its front
 *    door relocated onto a side wall by `resolveOpeningAdjustmentOpenings` —
 *    the same helper `heaterClearanceLayout` uses. Validating the raw opening
 *    checked a door the engine never builds and missed the one it does.
 * 2. **Only doors that reach this room.** With a relax room the exterior entry
 *    is on the RELAX side; projecting it into hot-room coordinates invented a
 *    swing collision in a room the door does not open into. Call this once per
 *    room — the bounds check is what assigns each door to one.
 *
 * `WallOpening.xMm` is measured from the low-coordinate corner of the OUTER
 * wall line, so the room's own inset comes off before the number means
 * anything to the layout model.
 */
/**
 * The opening set the engine actually frames, for a build input.
 *
 * `BuildInput.openings` is what the user authored; two adjustments happen before
 * a wall is built. A two-room with `entryWall: "long"` has its front door
 * REPLACED by one derived from the partition split, and panorama glazing removes
 * the right-wall windows because that wall becomes a single glass section.
 *
 * Exported so every consumer that draws or validates openings reads the same
 * list this function returns. The plan view (issue 132) held its own copy of
 * this expression and drew a door the built sauna does not contain — the same
 * two-paths-to-one-truth shape as the clearance overlay in issue 131. A copy
 * guarded by a parity test still drifts in any dimension the test does not
 * compare, so there is exactly one copy instead.
 *
 * The two-room guard matters: `resolveTwoRoomConfig` supplies defaults for
 * `entryWall` and `glazingStyle`, so without it a one-room design's front door
 * would be resolved against two-room defaults.
 */
export function resolveEngineOpenings(
	input: BuildInput,
): BuildInput["openings"] {
	return isTwoRoomInput(input)
		? resolveOpeningAdjustmentOpenings({
				...input,
				...resolveTwoRoomConfig(input),
			})
		: input.openings;
}

export function deriveInteriorDoors(
	input: BuildInput,
	room: InteriorRoom,
	envelope?: InteriorRoom,
): InteriorDoor[] {
	const openings = resolveEngineOpenings(input);
	// Bounds of the whole finished interior. A short wall belongs to the room
	// whose own bound sits ON it; without this the front wall's X span matched
	// BOTH rooms and every exterior door was projected into each of them.
	const outer = envelope ?? room;

	const doors: InteriorDoor[] = [];
	for (const opening of openings) {
		if (opening.type !== "door") continue;

		const wall = interiorWallFor(opening.wall, room, outer);
		if (!wall) continue;

		const outerHalfMm = wallRunsAlongX(wall)
			? (input.dimensions.width / 2) * 1000
			: (input.dimensions.length / 2) * 1000;
		const startMm = opening.xMm - outerHalfMm - wallAxisOriginMm(room, wall);

		// A door on a LONG wall belongs to whichever room its span falls in, so
		// measuring against this room's bounds is also what assigns it.
		const axisMm = wallAxisLengthMm(room, wall);
		if (startMm + opening.widthMm <= 0 || startMm >= axisMm) continue;

		// Issue 172: a door opens away from the hot room. An exterior door's
		// leaf sweeps the outside whichever room it serves; the divider door's
		// sweeps the relax room when it opens out of the sauna.
		const swing = resolveDoorSwing(opening);
		const swingsIntoRoom =
			opening.wall === "divider"
				? (swing === "in") === (room.id === "sauna")
				: swing === "in";
		doors.push({
			id: opening.id,
			wall,
			startMm,
			widthMm: opening.widthMm,
			swingsIntoRoom,
		});
	}
	return doors;
}

/**
 * Which of THIS room's walls an opening sits on, or null when the opening does
 * not bound it.
 *
 * Three cases:
 *  - long walls (`left`/`right`) bound every room; the span check assigns them.
 *  - short walls (`front`/`back`) bound only the room whose own edge lies on
 *    them — a divided building's front wall is not the far room's wall.
 *  - the `divider` is a wall of BOTH rooms, on opposite faces. It was skipped
 *    entirely, so a bench parked across the interior door raised no issue and
 *    nothing else checks it: `calculatePartitionWallFraming` never sees the
 *    layout.
 */
function interiorWallFor(
	openingWall: string,
	room: InteriorRoom,
	outer: InteriorRoom,
): InteriorWall | null {
	if (openingWall === "left" || openingWall === "right") {
		return openingWall;
	}
	if (openingWall === "front") {
		return room.maxZMm >= outer.maxZMm ? "front" : null;
	}
	if (openingWall === "back") {
		return room.minZMm <= outer.minZMm ? "back" : null;
	}
	if (openingWall === "divider") {
		// The divider is whichever short wall of this room is NOT the envelope's.
		if (room.minZMm > outer.minZMm) return "back";
		if (room.maxZMm < outer.maxZMm) return "front";
	}
	return null;
}

/**
 * The layout resolved into drawable geometry, for `buildSauna3D`.
 *
 * Deliberately the SAME `resolveInteriorLayout` the BOM bills from — issue 131
 * exists because the engines had a second, independent description of the same
 * benches. A second resolution path here would put it straight back.
 */
export function resolveInteriorRenderPlan(
	input: BuildInput,
	namePrefix: string,
): InteriorRenderPlan | null {
	const context = deriveInteriorLayoutContext(input);
	if (!context) return null;
	const layout = resolveInteriorLayout(input, context);
	if (!layout) return null;

	return createInteriorRenderPlan({
		layout,
		rooms: interiorRooms(context),
		heaterSize: context.heaterSize,
		generatedHeater: context.heater,
		namePrefix,
	});
}

/**
 * The layout's built plan geometry — bench footprints AND the heater's — for
 * the clearance check. Null when there is no layout; the check then keeps its
 * generated fallback.
 *
 * The heater box comes from the SAME `layout.heater` the benches were clipped
 * against. Passing benches alone left the check measuring authored benches
 * against the generated heater — two frames in one check, and a run parked at
 * the authored heater's clip margin could read as clear of a heater standing
 * somewhere else entirely.
 */
export function resolveAuthoredInteriorGeometry(
	layout: InteriorLayout | undefined,
	context?: InteriorLayoutContext,
): AuthoredInteriorGeometry | null {
	if (!layout || !context) return null;
	const benches: AuthoredBenchObstacle[] = [];
	for (const room of interiorRooms(context)) {
		// Only the hot room's benches can crowd the heater.
		if (room.id !== "sauna") continue;
		const { runs } = resolveEffectiveLayout(layout, room, context.heaterSize);
		for (const { effective, footprint } of runs) {
			// The whole run, not just its footprint and tier: its own leg height is
			// what decides how high the renderer draws it, and therefore whether it
			// stands in the aggregat's height band at all.
			benches.push({ footprint, run: effective });
		}
	}
	return {
		benches,
		heaterBox: heaterFootprint(layout.heater, context.room, context.heaterSize),
	};
}

/**
 * The clearance boxes for a design, measured against the layout it actually
 * has. Everything that DRAWS or reports the safety envelope goes through here.
 *
 * ONE owner, and the reason is a bug this issue introduced: the enrichment
 * chain fed the authored geometry in, while `HeaterClearanceOverlay` called
 * `deriveHeaterClearanceLayout(buildInput)` bare. Before #131 that was harmless
 * — the model drew the generated placement too — but the moment the renderer
 * started following `layout.heater`, the overlay kept drawing the BBR envelope
 * around the aggregat's OLD position. On a right-positioned two-room that put a
 * green "safe" box 1.7 m away, inside the relax room, while the verdict text
 * (which did read the layout) described the real unit.
 *
 * The general lesson: when a renderer starts following a new source of truth,
 * every other thing drawn from the old source becomes a lie — and the verdict
 * being right is not evidence that the drawing is.
 */
export function resolveHeaterClearanceLayout(
	input: BuildInput,
): HeaterClearanceLayout | null {
	const context = deriveInteriorLayoutContext(input);
	const layout = context ? resolveInteriorLayout(input, context) : null;
	return deriveHeaterClearanceLayout(
		input,
		resolveAuthoredInteriorGeometry(layout ?? undefined, context ?? undefined),
	);
}

export interface InteriorBomResult {
	layout: InteriorLayout;
	context: InteriorLayoutContext;
	bench: BenchMaterialsResult;
}

/**
 * Everything a calculation needs from the interior: the layout it is billing
 * for, the context it was measured in (handed on to the enrichment chain so it
 * is not derived twice), and the bench rows/cuts.
 *
 * See `RawSaunaEngine.calculateMaterials` for why the engines are handed this
 * rather than importing the resolver themselves.
 */
export function resolveInteriorBom(
	input: BuildInput,
	quantitySuffix?: string,
): InteriorBomResult | null {
	const context = deriveInteriorLayoutContext(input);
	if (!context) return null;

	const layout = resolveInteriorLayout(input, context);
	if (!layout) return null;

	// Each room is billed against its own walls. The relax side carries its own
	// suffix so a builder can tell the two buys apart on the list.
	const hot = deriveBenchMaterials({
		defaultWoodSpecies: normalizeFinishes(input.finishes).interiorWood,
		layout,
		room: context.room,
		heaterSize: context.heaterSize,
		quantitySuffix,
	});
	const relax = context.relaxRoom
		? deriveBenchMaterials({
				defaultWoodSpecies: normalizeFinishes(input.finishes).interiorWood,
				layout,
				room: context.relaxRoom,
				quantitySuffix: " (Relaxdel)",
			})
		: null;

	return {
		layout,
		context,
		bench: relax ? mergeBenchMaterials(hot, relax) : hot,
	};
}

function mergeBenchMaterials(
	a: BenchMaterialsResult,
	b: BenchMaterialsResult,
): BenchMaterialsResult {
	return {
		perRun: [...a.perRun, ...b.perRun],
		slatLpm: a.slatLpm + b.slatLpm,
		skirtLpm: a.skirtLpm + b.skirtLpm,
		backrestLpm: a.backrestLpm + b.backrestLpm,
		railLpm: a.railLpm + b.railLpm,
		ledgerLpm: a.ledgerLpm + b.ledgerLpm,
		frameLpm: a.frameLpm + b.frameLpm,
		fastenerCount: a.fastenerCount + b.fastenerCount,
		materials: [...a.materials, ...b.materials],
		cuts: [...a.cuts, ...b.cuts],
	};
}
